import { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Avatar } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import Wrapper from '../../components/Wrapper/Wrapper'
import NavBar from '../../components/NavBar/NavBar'
import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs'
import H1 from '../../components/H1/H1'
import ArticleDate from '../../components/ArticleDate/ArticleDate'
import ArticleSource from '../../components/ArticleSource/ArticleSource'

import CEOImage from '../../images/CEO.png'

import { getStorageWithExpiry } from '../../utils/getStorageWithExpiry'
import { setStorageWithExpiry } from '../../utils/setStorageWithExpiry'
import { parseHtmlContent } from '../../utils/parseHtmlContent'

import classes from './Article.module.css'

const Article = () => {
  const { slug } = useParams()
  const { t } = useTranslation()

  const breadcrumbScreenSectionRef = useRef(null)
  const articleRef = useRef(null)
  const [isWindowWidthMobile, setIsWindowWidthMobile] = useState(window.innerWidth <= 485)

  const source = t(`blog.article.${slug}.source`)
  const date = t(`blog.article.${slug}.date`)
  const title = t(`blog.article.${slug}.title`)
  const description = t(`blog.article.${slug}.description`)
  const referer = t(`blog.article.${slug}.referer`)
  const content = t(`blog.article.${slug}.content`, { returnObjects: true })

  const arrow = (
    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.625 10.5L4.375 10.5M4.375 10.5L10.5 16.625M4.375 10.5L10.5 4.375"
        stroke="#DD7700"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  const onWindowWidthChange = () => {
    setIsWindowWidthMobile(window.innerWidth <= 485)
  }
  useEffect(() => {
    if (!getStorageWithExpiry('lang')) {
      setStorageWithExpiry('lang', 'ru', dayjs().add(1, 'years').format())
    } else {
      setStorageWithExpiry(
        'lang',
        `${getStorageWithExpiry('lang')}`,
        dayjs().add(1, 'years').format()
      )
    }
    window.addEventListener('resize', onWindowWidthChange)
    return () => window.removeEventListener('resize', onWindowWidthChange)
  }, [])

  return (
    <div>
      <NavBar />
      <Wrapper wrapperRef={articleRef}>
        {isWindowWidthMobile ? (
          <Link to="/blog" className={`${classes['article__backlink']}`}>
            {arrow}
            <span>{t('article.backToBlog')}</span>
          </Link>
        ) : (
          <div className={`${classes['article__breadcrumbs']}`} ref={breadcrumbScreenSectionRef}>
            <Breadcrumbs
              breadcrumbs={[
                { label: t('breadcrumbs.home'), href: '/' },
                { label: t('breadcrumbs.blog'), href: '/blog' },
                { label: t('article.title') }
              ]}
            />
          </div>
        )}

        <div className={`${classes['article__container']}`}>
          <header className={`${classes['article__header']}`}>
            <H1 className={`${classes['article__title']}`}>{title}</H1>

            {description && <p className={`${classes['article__subtitle']}`}>{description}</p>}

            <div className={`${classes['article__meta']}`}>
              {source && <ArticleSource source={source} referer={referer} />}
              {date && <ArticleDate date={date} />}
            </div>
          </header>

          <div className={`${classes['article__divider']}`} />

          <div className={`${classes['article__body']}`}>
            <div className={`${classes['article__author']}`}>
              <Avatar size={isWindowWidthMobile ? 40 : 50} src={CEOImage} icon={<UserOutlined />} />
              <div className={`${classes['article__authorInfo']}`}>
                <a
                  href="https://south.vedomosti.ru/authors/1116580-andrei-kiporenko"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${classes['article__authorName']}`}
                >
                  {t('article.authorName')}
                  {', '}
                </a>
                <span> {t('article.authorTitle')}</span>
              </div>
            </div>

            <div className={classes.article__content}>
              {content.map((paragraph, index) => (
                <p key={index} className={classes.article__paragraph}>
                  {parseHtmlContent(paragraph, {
                    classNameB: classes['article__content-h2'],
                    classNameSpan: classes['article__content-span'],
                    classNameLink: classes['article__content-link']
                  })}
                </p>
              ))}
            </div>
          </div>
        </div>
      </Wrapper>
    </div>
  )
}

export default Article
